import type { ComponentType, NamedExoticComponent } from 'react';
import { memo } from 'react';
import mime from 'mime';
import {
  NODE_COLORS,
  type ResourceFileType,
} from '@refly-packages/ai-workspace-common/components/canvas/nodes/shared/colors';
import { CanvasNodeType, SelectionKey } from '@refly/openapi-schema';

import {
  AiChat,
  Group,
  Image,
  Video,
  Audio,
  Web1,
  Note,
  Media,
  Code1,
  Pdf,
  Doc1,
  Markdown,
  Text,
  Excel,
  CodeZip,
  GeneralFile,
  Html,
  File,
  MessageSmile,
} from 'refly-icons';
import { Avatar } from 'antd';
import { useThemeStoreShallow } from '@refly/stores';

type IconComponent = ComponentType<{ size?: number | string; color?: string }>;
const ICONS: Record<CanvasNodeType | SelectionKey, IconComponent> = {
  start: MessageSmile,
  group: Group,
  image: Image,
  video: Video,
  audio: Audio,
  document: Doc1,
  resource: File,
  file: File,
  codeArtifact: Code1,
  website: Web1,
  memo: Note,
  skillResponse: AiChat,
  // Add missing types with reasonable defaults
  tool: AiChat,
  toolResponse: AiChat,
  skill: AiChat,
  mediaSkill: Media,
  mediaSkillResponse: Media,
  documentSelection: Doc1,
  resourceSelection: Doc1,
  skillResponseSelection: AiChat,
  extensionWeblinkSelection: Web1,
  documentCursorSelection: Doc1,
  documentBeforeCursorSelection: Doc1,
  documentAfterCursorSelection: Doc1,
};

const FILE_ICONS: Record<ResourceFileType, IconComponent> = {
  'application/pdf': Pdf,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': Doc1,
  'text/markdown': Markdown,
  'text/plain': Text,
  'application/epub+zip': CodeZip,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': Excel,
  'application/vnd.ms-excel': Excel,
  'text/csv': Excel,
  'text/html': Html,
  // Images
  'image/jpeg': Image,
  'image/png': Image,
  'image/gif': Image,
  'image/webp': Image,
  'image/svg+xml': Image,
  'image/bmp': Image,
  // Videos
  'video/mp4': Video,
  'video/webm': Video,
  'video/ogg': Video,
  'video/quicktime': Video,
  'video/x-msvideo': Video,
  // Audio
  'audio/mpeg': Audio,
  'audio/wav': Audio,
  'audio/ogg': Audio,
  'audio/aac': Audio,
  'audio/webm': Audio,
};

interface NodeIconProps {
  type: CanvasNodeType | SelectionKey;
  className?: string;
  iconColor?: string;
  iconSize?: number;
  small?: boolean;
  filled?: boolean;
  url?: string;
  filename?: string;
  fileType?: string;
}

export const NodeIcon: NamedExoticComponent<NodeIconProps> = memo(
  ({
    type,
    className,
    iconColor = 'white',
    iconSize,
    small = true,
    filled = true,
    url,
    filename,
    fileType,
  }: NodeIconProps) => {
    const size = !filled ? 20 : small ? 14 : 16;
    const { isDarkMode } = useThemeStoreShallow((state) => ({
      isDarkMode: state.isDarkMode,
    }));

    const isResourceFile = type === 'file';

    const finalFileType = fileType ?? (filename ? mime.getType(filename) : null);
    const Icon = (isResourceFile ? FILE_ICONS[finalFileType] : ICONS[type]) ?? GeneralFile;

    const resolvedColor = isResourceFile
      ? (NODE_COLORS[finalFileType] ?? NODE_COLORS.resource)
      : (NODE_COLORS[type] ?? NODE_COLORS.document);

    if (url) {
      return (
        <div className="rounded-md flex items-center justify-center flex-shrink-0">
          <Avatar
            src={url}
            alt={type}
            icon={<Image size={size} />}
            className={`rounded-md object-cover ${small ? 'w-5 h-5' : 'w-6 h-6'} ${
              className ?? ''
            }`}
          />
        </div>
      );
    }

    if (!filled) {
      return (
        <div
          className={`rounded-md flex items-center justify-center flex-shrink-0 ${
            small ? 'w-5 h-5' : 'w-6 h-6'
          } ${className ?? ''}`}
        >
          <Icon size={iconSize || size} color={resolvedColor ?? iconColor} />
        </div>
      );
    }

    const backgroundColor =
      isDarkMode && ['start', 'skillResponse'].includes(type) ? 'black' : resolvedColor;

    return (
      <div
        className={`rounded-md flex items-center justify-center flex-shrink-0 ${
          small ? 'w-5 h-5' : 'w-6 h-6'
        } ${type === 'image' ? 'bg-gradient-to-r from-pink-500 to-purple-500' : ''} ${
          className ?? ''
        }`}
        style={{ backgroundColor: backgroundColor }}
      >
        <Icon size={iconSize || size} color="white" />
      </div>
    );
  },
);

NodeIcon.displayName = 'NodeIcon';
